import type { FC, ReactNode } from "react";
import PropTypes from "prop-types";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { Theme } from "@mui/material/styles/createTheme";
import { styled } from "@mui/material/styles";
import { useSections } from "../config";
import { MobileNav } from "../mobile-nav";
import { SideNav } from "./side-nav";
import { TopNav } from "./top-nav";
import { useMobileNav } from "./use-mobile-nav";
import { Box } from "@mui/material";
import { OrganizationGuard } from "../../../contexts/organization/OrganizationContext";
import { NavColor } from "../../../types/settings";
import { useAppSelector } from "src/app/hooks";
import { layoutSlice } from "src/features/layout/layoutSlice";
import { WorkspaceGuard } from "src/guards/workspace-guard";

const VerticalLayoutContainer = styled("div")({
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  width: "100%",
});

interface VerticalLayoutProps {
  children?: ReactNode;
  navColor?: NavColor;
}

export const VerticalLayout: FC<VerticalLayoutProps> = (props) => {
  const sections = useSections();
  const { children, navColor } = props;
  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("lg"));
  const mobileNav = useMobileNav();
  const SIDE_NAV_WIDTH = useAppSelector(layoutSlice.selectors.sidenavWidth);

  const VerticalLayoutRoot = styled("div")(({ theme }) => ({
    display: "flex",
    flex: "1 1 auto",
    maxWidth: "100%",
    [theme.breakpoints.up("lg")]: {
      paddingLeft: SIDE_NAV_WIDTH,
    },
  }));

  return (
    <>
      <TopNav onMobileNavOpen={mobileNav.handleOpen} />
      {lgUp && <SideNav color={navColor} sections={sections} />}
      {!lgUp && (
        <MobileNav
          color={navColor}
          onClose={mobileNav.handleClose}
          open={mobileNav.open}
          sections={sections}
        />
      )}
      <VerticalLayoutRoot>
        <VerticalLayoutContainer>
          <Box px={3}></Box>
          <WorkspaceGuard>{children}</WorkspaceGuard>
        </VerticalLayoutContainer>
      </VerticalLayoutRoot>
    </>
  );
};
